import React from "react";
import styled from "@emotion/styled";
import {
  text,
  surface,
  border,
  zIndex,
  shadow,
  fontWeight,
  fontSize,
  spacing,
  borderRadius,
  lineHeight,
  dataViz,
} from "@atlas-int/tokens";
import BaseCloseButton from "./CloseButton";

const ModalRootStyled = styled.div`
  position: fixed;
  z-index: ${zIndex.dropdown};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
`;

interface ModalRootProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Called when the user presses Escape inside the modal */
  onClose?: () => void;
}

export const ModalRoot = React.forwardRef<HTMLDivElement, ModalRootProps>(
  ({ onClose, onKeyDown, ...rest }, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape" && onClose) {
        e.stopPropagation();
        onClose();
      }
      onKeyDown?.(e);
    };

    return (
      <ModalRootStyled
        ref={ref}
        role="dialog"
        aria-modal={true}
        onKeyDown={handleKeyDown}
        {...rest}
      />
    );
  },
);
ModalRoot.displayName = "ModalRoot";

export const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: ${surface.overlay};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
`;

export const ModalContainerBase = styled.div`
  background-color: ${surface.raised};
  padding: ${spacing[8]};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  border-radius: ${borderRadius.base};
  border: 1px solid ${border.subtle};
  box-shadow: ${shadow.sm};
`;

export const ModalContentContainer = styled(ModalContainerBase)<{
  /** Override the default width (e.g. "600px", "80%"). Default: 45% */
  $width?: string;
}>`
  position: relative;
  padding: 0px;
  margin: 0px;
  text-align: left;
  height: auto;
  max-height: calc(100vh - 2 * ${spacing[20]});
  width: ${({ $width }) => $width || "45%"};
  margin-top: ${spacing[20]};
  margin-bottom: ${spacing[20]};

  @media (max-width: 800px) {
    width: ${({ $width }) => $width || "90%"};
    margin-top: ${spacing[8]};
    margin-bottom: ${spacing[8]};
    max-height: calc(100vh - 2 * ${spacing[8]});
  }

  & h2 {
    width: 100%;
  }

  a:link,
  a:visited {
    color: ${text.link};
  }
`;

export const CloseButton = styled(BaseCloseButton)`
  flex: 0 1 auto;
  color: ${text.muted};
`;

export const ModalHeaderIcon = styled.div`
  flex: 0 1 auto;
  width: ${spacing[16]};
  height: 100%;
  padding: ${spacing[6]};
  padding-left: ${spacing[0]};
  margin-right: ${spacing[4]};
  box-sizing: border-box;
  background-color: ${dataViz.primaryAction};
  mask-repeat: no-repeat;
  mask-size: contain;
  mask-position: center center;
`;

export const ModalHeader = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-radius: ${borderRadius.base} ${borderRadius.base} 0 0;
  margin: 0px;
  padding: 0 2.5vw 0 2.5vw;
  background-color: ${dataViz.tooltipHeader};
  display: flex;
  flex-direction: row;

  & h1 {
    text-transform: uppercase;
    color: ${dataViz.primaryAction};
    flex: 1 1 auto;
    font-weight: ${fontWeight.light};
    display: inline-block;
    font-size: ${fontSize["3xl"]};
    margin: 0px;
    padding: ${spacing[6]} 0px;
  }
`;

export const ModalBodyContainer = styled.div`
  width: 100%;
  padding: 2.5vw 2.5vw 2.5vw 2.5vw;
  box-sizing: border-box;
  height: auto;
  max-height: 100%;
  overflow: hidden;

  & p {
    margin-top: 0px;
  }
  flex-grow: 1;
  flex-shrink: 1;
`;

export const ModalBody = styled.div`
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  & p {
    color: ${text.primary};
  }
`;

export const GlossaryModalBody = styled(ModalBody)`
  & > div {
    width: 100%;
    padding: 0px;
    margin: 0px;
    margin-bottom: ${spacing[4]};
  }

  summary {
    font-size: ${fontSize["2xl"]};
    line-height: ${lineHeight.tight};
  }
`;

export { ClearButtonSymbol } from "./CloseButton";
